import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
    Box, Drawer, AppBar, Toolbar, List, Typography,
    ListItem, ListItemIcon, ListItemText, IconButton
} from '@mui/material';
import {
    Menu as MenuIcon,
    Dashboard as DashboardIcon,
    People as PeopleIcon,
    Gavel as GavelIcon,
    AccountBalance as AccountBalanceIcon,
    Warning as WarningIcon,
    Settings as SettingsIcon,
    Logout as LogoutIcon,
    Cake as CakeIcon,
    ExpandLess,
    ExpandMore,
    Tune as TuneIcon
} from '@mui/icons-material';
import {
    Collapse
} from '@mui/material';
import ThemeToggle from './ThemeToggle';
import NotificacoesBar from './NotificacoesBar';
import { isAdmin, hasFluxoCaixaAccess } from '../utils/auth';
import { useInatividade } from '../hooks/useInatividade';
import { useHorarioPermitido } from '../hooks/useHorarioPermitido';
import api from '../services/api';
import {
    History as HistoryIcon,
    Person as PersonIcon,
    Event as EventIcon,
    AttachMoney as AttachMoneyIcon,
    Storage as StorageIcon,
    CalendarToday as CalendarTodayIcon
} from '@mui/icons-material';

const drawerWidth = 240;

function Layout({ children }) {
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = React.useState(false);
    const [configOpen, setConfigOpen] = React.useState(false);
    const [controlesOpen, setControlesOpen] = React.useState(false);
    const [hasFluxoCaixa, setHasFluxoCaixa] = React.useState(false);
    const [configuracoes, setConfiguracoes] = React.useState({
        tempo_inatividade: 30,
        hora_inicio: '07:00',
        hora_fim: '18:00',
        bloqueio_horario_ativo: true
    });
    const [carregandoConfig, setCarregandoConfig] = React.useState(true);

    // Ref para armazenar configurações anteriores (para comparação)
    const configAnteriorRef = React.useRef(null);

    // Carrega configurações GLOBAIS do sistema e do usuário
    useEffect(() => {
        const carregarConfiguracoes = async () => {
            try {
                // Carrega configurações globais (horário de uso)
                const responseGlobal = await api.get('/configuracao-sistema');
                const configGlobal = responseGlobal.data;
                
                // Carrega configurações do usuário (tempo de inatividade)
                const responseUsuario = await api.get('/configuracao-usuario/minhas-configuracoes');
                const configUsuario = responseUsuario.data;
                
                // Combina configurações: globais para horário, do usuário para inatividade
                const novasConfig = {
                    ...configUsuario, // tempo_inatividade
                    hora_inicio: configGlobal.hora_inicio,
                    hora_fim: configGlobal.hora_fim,
                    bloqueio_horario_ativo: configGlobal.bloqueio_horario_ativo
                };
                
                // Verifica se houve mudança nas configurações de horário
                if (configAnteriorRef.current) {
                    const mudouHorario = 
                        novasConfig.hora_inicio !== configAnteriorRef.current.hora_inicio ||
                        novasConfig.hora_fim !== configAnteriorRef.current.hora_fim ||
                        novasConfig.bloqueio_horario_ativo !== configAnteriorRef.current.bloqueio_horario_ativo;
                    
                    if (mudouHorario) {
                        console.log('[CONFIG] ⚠️ Configurações GLOBAIS de horário alteradas pelo admin!', {
                            antigas: configAnteriorRef.current,
                            novas: novasConfig
                        });
                    }
                }
                
                configAnteriorRef.current = novasConfig;
                setConfiguracoes(novasConfig);
                console.log('[CONFIG] Configurações carregadas (Global + Usuário):', novasConfig);
            } catch (error) {
                console.error('Erro ao carregar configurações:', error);
                // Se for erro 403 (horário bloqueado), não tenta carregar
                if (error.response?.status === 403) {
                    return;
                }
                // Usa valores padrão em caso de erro
            } finally {
                setCarregandoConfig(false);
            }
        };
        
        carregarConfiguracoes();
        
        // Recarrega configurações a cada 15 segundos (para pegar mudanças do admin rapidamente)
        const interval = setInterval(carregarConfiguracoes, 15000); // 15 segundos
        return () => clearInterval(interval);
    }, []); // Sem dependências para evitar loop - recarrega sempre a cada 15s

    // Hook de inatividade (só ativa após carregar configurações)
    useInatividade(
        carregandoConfig ? 30 : configuracoes.tempo_inatividade,
        !carregandoConfig // Só ativa após carregar
    );

    // Hook de verificação de horário (só ativa após carregar configurações)
    // Log quando configurações mudam
    useEffect(() => {
        if (!carregandoConfig) {
            console.log('[LAYOUT] Configurações de horário atualizadas:', {
                hora_inicio: configuracoes.hora_inicio,
                hora_fim: configuracoes.hora_fim,
                bloqueio_horario_ativo: configuracoes.bloqueio_horario_ativo
            });
        }
    }, [configuracoes.hora_inicio, configuracoes.hora_fim, configuracoes.bloqueio_horario_ativo, carregandoConfig]);

    useHorarioPermitido(
        carregandoConfig ? '07:00' : configuracoes.hora_inicio,
        carregandoConfig ? '18:00' : configuracoes.hora_fim,
        carregandoConfig ? true : configuracoes.bloqueio_horario_ativo
    );

    // Verifica permissão de acesso ao Fluxo de Caixa
    // Recarrega quando a localização muda (usuário navega para outra página)
    useEffect(() => {
        const checkFluxoCaixa = async () => {
            try {
                const access = await hasFluxoCaixaAccess();
                setHasFluxoCaixa(access);
            } catch (error) {
                if (process.env.NODE_ENV === 'development') {
                    console.error('Erro ao verificar acesso ao Fluxo de Caixa:', error);
                }
                setHasFluxoCaixa(false);
            }
        };
        checkFluxoCaixa();
    }, [location.pathname]); // Recarrega quando a rota muda

    const menuItems = [
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
        { text: 'Clientes', icon: <PeopleIcon />, path: '/clientes' },
        { text: 'Processos', icon: <GavelIcon />, path: '/processos' },
        { text: 'Prazos', icon: <EventIcon />, path: '/prazos' },
        { text: 'Calendário de Prazos', icon: <CalendarTodayIcon />, path: '/prazos/calendario' },
        ...(hasFluxoCaixa ? [{ text: 'Fluxo de Caixa', icon: <AttachMoneyIcon />, path: '/fluxo-caixa' }] : []),
        { text: 'Histórico de Acessos', icon: <HistoryIcon />, path: '/historico-acesso' },
        { text: 'Advogados', icon: <AccountBalanceIcon />, path: '/advogados' },
        { text: 'Registros Duplicados', icon: <WarningIcon />, path: '/bkp-duplicados' }
    ];

    const controlesItems = [
        { text: 'Aniversariantes', icon: <CakeIcon />, path: '/aniversariantes', adminOnly: false },
        ...(isAdmin() ? [
            { text: 'Auditoria', icon: <HistoryIcon />, path: '/auditoria', adminOnly: true },
            { text: 'Usuários', icon: <PersonIcon />, path: '/usuarios', adminOnly: true },
            { text: 'Backup', icon: <StorageIcon />, path: '/backup', adminOnly: true }
        ] : [])
    ];

    const configItems = [
        { text: 'Minhas Configurações', path: '/configuracao-usuario' },
        { text: 'Status', path: '/configuracoes/status' },
        { text: 'Tipo de Benefício', path: '/configuracoes/tipo-beneficio' },
        { text: 'Localização', path: '/configuracoes/localizacao' },
        { text: 'Vara', path: '/configuracoes/vara' },
        { text: 'Tipo de Andamento', path: '/configuracoes/tipo-andamento' },
        { text: 'Tipo de Ação', path: '/configuracoes/tipo-acao' },
        { text: 'Partes Externas', path: '/configuracoes/parte-externa' },
        { text: 'API de Andamentos', path: '/configuracoes/api-andamentos' }
    ];

    const handleLogout = () => {
        // Remove token de autenticação
        localStorage.removeItem('token');
        
        // Limpa filtros salvos (específicos por página)
        localStorage.removeItem('prazos_filtros');
        
        // Limpa dados de sessão (scroll positions, etc)
        sessionStorage.removeItem('prazos_scroll_position');
        
        // Limpa qualquer outro dado de sessão que possa existir
        // (não afeta localStorage, então preferências como themeMode são preservadas)
        sessionStorage.clear();
        
        navigate('/login');
    };

    return (
        <Box sx={{ display: 'flex' }}>
            <AppBar position="fixed" sx={{ zIndex: 1201 }}>
                <Toolbar>
                    <IconButton
                        color="inherit"
                        edge="start"
                        onClick={() => setMobileOpen(!mobileOpen)}
                        sx={{ mr: 2, display: { sm: 'none' } }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
                        Sistema Jurídico
                    </Typography>
                    <NotificacoesBar />
                    <ThemeToggle />
                    <IconButton color="inherit" onClick={handleLogout} title="Sair">
                        <LogoutIcon />
                    </IconButton>
                </Toolbar>
            </AppBar>

            {/* Drawer para desktop */}
            <Drawer
                variant="permanent"
                sx={{
                    display: { xs: 'none', sm: 'block' },
                    '& .MuiDrawer-paper': { 
                        width: drawerWidth,
                        boxSizing: 'border-box' 
                    },
                }}
                open
            >
                <Toolbar />
                <List>
                    {menuItems.map((item) => {
                        const isActive = location.pathname === item.path || 
                                       (item.path !== '/' && location.pathname.startsWith(item.path));
                        return (
                            <ListItem 
                                button 
                                key={item.text}
                                onClick={() => navigate(item.path)}
                                selected={isActive}
                                sx={{
                                    backgroundColor: isActive ? 'primary.main' : 'transparent',
                                    color: isActive ? 'primary.contrastText' : 'inherit',
                                    '&:hover': {
                                        backgroundColor: isActive ? 'primary.dark' : 'action.hover',
                                    },
                                    '&.Mui-selected': {
                                        backgroundColor: 'primary.main',
                                        color: 'primary.contrastText',
                                        '&:hover': {
                                            backgroundColor: 'primary.dark',
                                        },
                                        '& .MuiListItemIcon-root': {
                                            color: 'primary.contrastText',
                                        },
                                    },
                                }}
                            >
                                <ListItemIcon sx={{ color: isActive ? 'primary.contrastText' : 'inherit' }}>
                                    {item.icon}
                                </ListItemIcon>
                                <ListItemText 
                                    primary={item.text}
                                    primaryTypographyProps={{
                                        fontWeight: isActive ? 600 : 400,
                                    }}
                                />
                            </ListItem>
                        );
                    })}
                    <ListItem button onClick={() => setControlesOpen(!controlesOpen)}>
                        <ListItemIcon>
                            <TuneIcon />
                        </ListItemIcon>
                        <ListItemText primary="Controles" />
                        {controlesOpen ? <ExpandLess /> : <ExpandMore />}
                    </ListItem>
                    <Collapse in={controlesOpen} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            {controlesItems.map((item) => {
                                const isActive = location.pathname === item.path || 
                                               (item.path !== '/' && location.pathname.startsWith(item.path));
                                return (
                                    <ListItem 
                                        button 
                                        key={item.text}
                                        sx={{ 
                                            pl: 4,
                                            backgroundColor: isActive ? 'primary.main' : 'transparent',
                                            color: isActive ? 'primary.contrastText' : 'inherit',
                                            '&:hover': {
                                                backgroundColor: isActive ? 'primary.dark' : 'action.hover',
                                            },
                                        }}
                                        onClick={() => navigate(item.path)}
                                    >
                                        <ListItemIcon sx={{ color: isActive ? 'primary.contrastText' : 'inherit', minWidth: 40 }}>
                                            {item.icon}
                                        </ListItemIcon>
                                        <ListItemText 
                                            primary={item.text}
                                            primaryTypographyProps={{
                                                fontWeight: isActive ? 600 : 400,
                                            }}
                                        />
                                    </ListItem>
                                );
                            })}
                        </List>
                    </Collapse>
                    <ListItem button onClick={() => setConfigOpen(!configOpen)}>
                        <ListItemIcon>
                            <SettingsIcon />
                        </ListItemIcon>
                        <ListItemText primary="Configurações" />
                        {configOpen ? <ExpandLess /> : <ExpandMore />}
                    </ListItem>
                    <Collapse in={configOpen} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            {configItems.map((item) => {
                                const isActive = location.pathname === item.path;
                                return (
                                    <ListItem 
                                        button 
                                        key={item.text}
                                        sx={{ 
                                            pl: 4,
                                            backgroundColor: isActive ? 'primary.main' : 'transparent',
                                            color: isActive ? 'primary.contrastText' : 'inherit',
                                            '&:hover': {
                                                backgroundColor: isActive ? 'primary.dark' : 'action.hover',
                                            },
                                        }}
                                        onClick={() => navigate(item.path)}
                                    >
                                        <ListItemText 
                                            primary={item.text}
                                            primaryTypographyProps={{
                                                fontWeight: isActive ? 600 : 400,
                                            }}
                                        />
                                    </ListItem>
                                );
                            })}
                        </List>
                    </Collapse>
                </List>
            </Drawer>

            {/* Drawer para mobile */}
            <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={() => setMobileOpen(false)}
                ModalProps={{
                    keepMounted: true, // Melhor performance no mobile
                }}
                sx={{
                    display: { xs: 'block', sm: 'none' },
                    '& .MuiDrawer-paper': { 
                        width: drawerWidth,
                        boxSizing: 'border-box' 
                    },
                }}
            >
                <Toolbar />
                <List>
                    {menuItems.map((item) => {
                        const isActive = location.pathname === item.path || 
                                       (item.path !== '/' && location.pathname.startsWith(item.path));
                        return (
                            <ListItem 
                                button 
                                key={item.text}
                                onClick={() => {
                                    navigate(item.path);
                                    setMobileOpen(false);
                                }}
                                selected={isActive}
                                sx={{
                                    backgroundColor: isActive ? 'primary.main' : 'transparent',
                                    color: isActive ? 'primary.contrastText' : 'inherit',
                                    '&:hover': {
                                        backgroundColor: isActive ? 'primary.dark' : 'action.hover',
                                    },
                                    '&.Mui-selected': {
                                        backgroundColor: 'primary.main',
                                        color: 'primary.contrastText',
                                        '&:hover': {
                                            backgroundColor: 'primary.dark',
                                        },
                                        '& .MuiListItemIcon-root': {
                                            color: 'primary.contrastText',
                                        },
                                    },
                                }}
                            >
                                <ListItemIcon sx={{ color: isActive ? 'primary.contrastText' : 'inherit' }}>
                                    {item.icon}
                                </ListItemIcon>
                                <ListItemText 
                                    primary={item.text}
                                    primaryTypographyProps={{
                                        fontWeight: isActive ? 600 : 400,
                                    }}
                                />
                            </ListItem>
                        );
                    })}
                    <ListItem button onClick={() => setControlesOpen(!controlesOpen)}>
                        <ListItemIcon>
                            <TuneIcon />
                        </ListItemIcon>
                        <ListItemText primary="Controles" />
                        {controlesOpen ? <ExpandLess /> : <ExpandMore />}
                    </ListItem>
                    <Collapse in={controlesOpen} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            {controlesItems.map((item) => {
                                const isActive = location.pathname === item.path || 
                                               (item.path !== '/' && location.pathname.startsWith(item.path));
                                return (
                                    <ListItem 
                                        button 
                                        key={item.text}
                                        sx={{ 
                                            pl: 4,
                                            backgroundColor: isActive ? 'primary.main' : 'transparent',
                                            color: isActive ? 'primary.contrastText' : 'inherit',
                                            '&:hover': {
                                                backgroundColor: isActive ? 'primary.dark' : 'action.hover',
                                            },
                                        }}
                                        onClick={() => {
                                            navigate(item.path);
                                            setMobileOpen(false);
                                        }}
                                    >
                                        <ListItemIcon sx={{ color: isActive ? 'primary.contrastText' : 'inherit', minWidth: 40 }}>
                                            {item.icon}
                                        </ListItemIcon>
                                        <ListItemText 
                                            primary={item.text}
                                            primaryTypographyProps={{
                                                fontWeight: isActive ? 600 : 400,
                                            }}
                                        />
                                    </ListItem>
                                );
                            })}
                        </List>
                    </Collapse>
                    <ListItem button onClick={() => setConfigOpen(!configOpen)}>
                        <ListItemIcon>
                            <SettingsIcon />
                        </ListItemIcon>
                        <ListItemText primary="Configurações" />
                        {configOpen ? <ExpandLess /> : <ExpandMore />}
                    </ListItem>
                    <Collapse in={configOpen} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            {configItems.map((item) => {
                                const isActive = location.pathname === item.path;
                                return (
                                    <ListItem 
                                        button 
                                        key={item.text}
                                        sx={{ 
                                            pl: 4,
                                            backgroundColor: isActive ? 'primary.main' : 'transparent',
                                            color: isActive ? 'primary.contrastText' : 'inherit',
                                            '&:hover': {
                                                backgroundColor: isActive ? 'primary.dark' : 'action.hover',
                                            },
                                        }}
                                        onClick={() => {
                                            navigate(item.path);
                                            setMobileOpen(false);
                                        }}
                                    >
                                        <ListItemText 
                                            primary={item.text}
                                            primaryTypographyProps={{
                                                fontWeight: isActive ? 600 : 400,
                                            }}
                                        />
                                    </ListItem>
                                );
                            })}
                        </List>
                    </Collapse>
                </List>
            </Drawer>

            <Box
                component="main"
                sx={{ 
                    flexGrow: 1, 
                    p: { xs: 1, sm: 2, md: 3 }, 
                    width: { sm: `calc(100% - ${drawerWidth}px)` },
                    marginLeft: { sm: `${drawerWidth}px` },
                    marginTop: '64px',
                    maxWidth: '100%',
                    overflowX: 'hidden'
                }}
            >
                {children}
            </Box>
        </Box>
    );
}

export default Layout; 